/**
 * CarouselVideoPreview
 *
 * Client-side preview component that renders a carousel-to-video
 * conversion using the Remotion Player. Provides play/pause/scrub controls.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Player, Thumbnail } from '@remotion/player';
import type { PlayerRef } from '@remotion/player';
import { Play } from 'lucide-react';
import {
  CarouselVideo,
  CarouselVideoProps,
  SlideData,
  calculateCarouselVideoDuration,
} from './compositions/CarouselVideo';
import { ChoreographySpec, VideoChoreography, isDirectedSpec } from '../models/ChoreographySpec';
import { DirectedVideo, calculateDirectedVideoDuration } from './compositions/DirectedVideo';

export function createPreviewCoverChoreography(
  firstSlide: SlideData,
  fps: number,
): ChoreographySpec {
  return {
    fps,
    slides: [
      {
        slideIndex: 0,
        durationSeconds: 1,
        overlayEntrances: firstSlide.overlays.map((overlay) => ({
          overlayId: overlay.id,
          type: 'none' as const,
          delaySeconds: 0,
          durationSeconds: 0,
        })),
        transitionOut: { type: 'none', durationSeconds: 0 },
      },
    ],
  };
}

export interface CarouselVideoPreviewProps {
  /** Slide data extracted from carousel post MediaItems */
  slides: SlideData[];
  /** The video's choreography: slide by slide, or a directed video */
  choreography: VideoChoreography;
  /** Original slide width (used as fallback for composition) */
  containerWidth: number;
  /** Original slide height (used as fallback for composition) */
  containerHeight: number;
  /** Output video width — defaults to slide width */
  outputWidth?: number;
  /** Output video height — defaults to 9:16 ratio (1920 for 1080 width) */
  outputHeight?: number;
  /** Fill mode when slide aspect differs from output */
  fillMode?: 'adaptive' | 'blur' | 'color' | 'none';
  /** Optional className */
  className?: string;
  /** Optional style */
  style?: React.CSSProperties;
  /** Reference media width for scaling */
  referenceWidth?: number;
  /** Reference media height for scaling */
  referenceHeight?: number;
  /** Layout engine */
  layoutEngine?: 'fabric' | 'remotion';
  /** Auto-play on mount */
  autoPlay?: boolean;
  /** Signed URL for background music */
  musicUrl?: string;
  /** Hold on the live, fully composed first frame until the user presses play. */
  startOnCover?: boolean;
}

export const CarouselVideoPreview: React.FC<CarouselVideoPreviewProps> = ({
  slides,
  choreography,
  containerWidth,
  containerHeight,
  outputWidth,
  outputHeight,
  fillMode = 'adaptive',
  className,
  style,
  referenceWidth,
  referenceHeight,
  layoutEngine,
  autoPlay = false,
  musicUrl,
  startOnCover = true,
}) => {
  const playerRef = useRef<PlayerRef>(null);
  const slideWidth = slides[0]?.dimensions.width ?? Math.round(containerWidth);
  const slideHeight = slides[0]?.dimensions.height ?? Math.round(containerHeight);

  // Output dimensions: default to 9:16 if not specified
  const compWidth = outputWidth ?? slideWidth;
  const compHeight = outputHeight ?? Math.round(slideWidth * (16 / 9));
  const fps = choreography.fps || 30;

  const directed = isDirectedSpec(choreography);
  const durationInFrames = useMemo(
    () => (isDirectedSpec(choreography) ? calculateDirectedVideoDuration(choreography) : calculateCarouselVideoDuration(choreography)),
    [choreography],
  );

  const inputProps: CarouselVideoProps = useMemo(
    () => ({
      slides,
      // The cover (below) always shows the static first slide in the carousel format
      choreography: (isDirectedSpec(choreography) ? (slides[0] ? createPreviewCoverChoreography(slides[0], fps) : { fps, slides: [] }) : choreography) as ChoreographySpec,
      width: compWidth,
      height: compHeight,
      referenceWidth: referenceWidth ?? slideWidth,
      referenceHeight: referenceHeight ?? slideHeight,
      layoutEngine,
      fillMode,
      musicUrl,
    }),
    [slides, choreography, fps, compWidth, compHeight, referenceWidth, referenceHeight, slideWidth, slideHeight, layoutEngine, fillMode, musicUrl],
  );
  const directedInputProps = useMemo(
    () => (isDirectedSpec(choreography) ? { slides, spec: choreography, width: compWidth, height: compHeight, musicUrl } : null),
    [slides, choreography, compWidth, compHeight, musicUrl],
  );

  const coverInputProps = useMemo<CarouselVideoProps | null>(() => {
    const firstSlide = slides[0];
    if (!firstSlide) return null;
    return {
      ...inputProps,
      slides: [firstSlide],
      choreography: createPreviewCoverChoreography(firstSlide, fps),
      musicUrl: undefined,
    };
  }, [fps, inputProps, slides]);

  const playerKey = useMemo(
    () =>
      [
        compWidth,
        compHeight,
        durationInFrames,
        fps,
        layoutEngine ?? 'default',
        fillMode,
      ].join('|'),
    [compWidth, compHeight, durationInFrames, fps, layoutEngine, fillMode],
  );
  const [coverDismissed, setCoverDismissed] = useState(false);
  useEffect(() => {
    setCoverDismissed(false);
    if (startOnCover) {
      playerRef.current?.pause();
      playerRef.current?.seekTo(0);
    }
  }, [startOnCover, playerKey, slides, choreography]);
  const showCover = startOnCover && !coverDismissed && coverInputProps !== null;

  const playFromCover = () => {
    setCoverDismissed(true);
    playerRef.current?.seekTo(0);
    playerRef.current?.play();
  };

  if (!compWidth || !compHeight || compWidth <= 0 || compHeight <= 0) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        width: style?.width ?? containerWidth,
        height: style?.height ?? containerHeight,
        position: 'relative',
        ...style,
      }}
    >
      <Player
        ref={playerRef}
        key={playerKey}
        component={(directed ? DirectedVideo : CarouselVideo) as React.ComponentType<Record<string, unknown>>}
        inputProps={(directedInputProps ?? inputProps) as Record<string, unknown>}
        durationInFrames={durationInFrames}
        fps={fps}
        compositionWidth={compWidth}
        compositionHeight={compHeight}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
        }}
        controls={!showCover}
        autoPlay={!showCover && (autoPlay || coverDismissed)}
        loop={true}
        renderLoading={() => null}
        allowFullscreen={false}
      />
      {showCover && (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Thumbnail
              component={CarouselVideo}
              inputProps={coverInputProps}
              durationInFrames={fps}
              frameToDisplay={0}
              fps={fps}
              compositionWidth={compWidth}
              compositionHeight={compHeight}
              style={{ width: '100%', height: '100%' }}
              renderLoading={() => null}
            />
          </div>
          <button
            type="button"
            aria-label="Play video"
            onClick={playFromCover}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              border: 0,
              padding: 0,
              cursor: 'pointer',
              overflow: 'hidden',
              background: 'transparent',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                display: 'grid',
                width: 52,
                height: 52,
                placeItems: 'center',
                borderRadius: '50%',
                color: '#fefdfb',
                background: 'rgba(34, 31, 27, 0.72)',
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 8px 24px rgba(34, 31, 27, 0.24)',
              }}
            >
              <Play size={22} fill="currentColor" />
            </span>
          </button>
        </>
      )}
    </div>
  );
};

export default CarouselVideoPreview;
